import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bath, Wind, BookOpen, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function AlternativeUnwindingGuide() {
  const navigate = useNavigate();

  const relaxationMethods = [
    { icon: Bath, title: "Warm Bath", desc: "Add Epsom salts, light candles, play soft music. 20 minutes of pure relaxation." },
    { icon: Wind, title: "Deep Breathing", desc: "4-7-8 technique: Inhale 4 counts, hold 7, exhale 8. Repeat 4 times." },
    { icon: BookOpen, title: "Reading", desc: "Fiction transports you. Non-fiction engages your mind. Both beat scrolling." },
    { icon: Moon, title: "Evening Walk", desc: "15 minutes outside shifts your energy. Notice the sky, the air, the quiet." }
  ];

  const quickOptions = [
    "Make a cup of herbal tea and drink it slowly",
    "10 minutes of gentle stretching",
    "Call a friend for a quick chat",
    "Put on your favorite playlist",
    "Sit outside and watch the sky",
    "Write in your journal for 5 minutes",
    "Do a 5-minute guided meditation",
    "Take 10 slow, deep breaths"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
            <Bath className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Alternative Unwinding</h1>
          <p className="text-lg text-secondary-600">Discover what truly relaxes you beyond alcohol</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">The Real Question</h2>
          <p className="text-secondary-700 mb-4">
            When you reach for a drink to "unwind," what are you actually seeking? Alcohol creates an illusion of relaxation while actually increasing stress hormones. True relaxation comes from activities that genuinely calm your nervous system.
          </p>
          <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
            <p className="text-teal-800 font-medium">
              💡 Tonight, when you'd normally reach for a drink, try one alternative instead. Notice what actually relaxes you.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Deep Relaxation Methods</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {relaxationMethods.map((method, i) => (
              <div key={i} className="p-4 bg-teal-50 rounded-xl border border-teal-200">
                <div className="flex items-center gap-3 mb-2">
                  <method.icon className="w-6 h-6 text-teal-600" />
                  <h3 className="font-bold text-secondary-900">{method.title}</h3>
                </div>
                <p className="text-secondary-600 text-sm">{method.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border-2 border-teal-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Quick Options (5-10 minutes)</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {quickOptions.map((opt, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                <span className="text-teal-500">✓</span>
                <span className="text-secondary-700">{opt}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Today's Experiment</h2>
          <div className="space-y-4">
            <p className="text-secondary-700">At your usual "unwinding" time tonight:</p>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
                <span className="text-secondary-700">Notice the urge without acting on it (just 2 minutes)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
                <span className="text-secondary-700">Ask: "What am I really seeking right now?"</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
                <span className="text-secondary-700">Choose one alternative from the lists above</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
                <span className="text-secondary-700">Give it your full attention for at least 15 minutes</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">5</span>
                <span className="text-secondary-700">Notice how you feel afterward</span>
              </li>
            </ol>
          </div>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "What alternative did I try tonight? How did it compare to how I usually feel after drinking? What actually relaxes me?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default AlternativeUnwindingGuide;
